import { useState } from 'react';

import { correctingTrailingNumbers } from '@cryptoboyio/mercuryo-lab/utils';

import { useDebounce } from '@utils/hooks';

import { BuyTypes, GlobalTypes } from '@modules';

const GET_RATE_DEBOUNCE_TIME = 1000;

export type RateType = {
  total: { [k in GlobalTypes.CurrencyEnum]?: string };
  subtotal: { [k in GlobalTypes.CurrencyEnum]?: string };
};

export type ErrorDataType = {
  [k in GlobalTypes.CurrencyEnum]?: {
    min?: string;
    max?: string;
  };
};

export type ErrorType = {
  message?: string;
  data: ErrorDataType | ErrorDataType[];
};

export type GetRate = (
  from: GlobalTypes.CurrencyEnum,
  to: GlobalTypes.CurrencyEnum,
  amount: string
) => Promise<{ data: RateType }>;

export type GetError = (currency: GlobalTypes.CurrencyEnum) => string;

export type Convert = (
  from: BuyTypes.GETBuyRateParams['from'],
  to: BuyTypes.GETBuyRateParams['to'],
  amount: BuyTypes.GETBuyRateParams['amount']
) => void;

export type UseConvert = (
  getRate?: GetRate,
  displayOptions?: GlobalTypes.CurrencyDisplayOptionsType
) => [Convert, RateType | undefined, string];

const useConvert: UseConvert = (getRate, displayOptions) => {
  const [rate, setRate] = useState<RateType>();
  const [error, setError] = useState('');
  const debounced = useDebounce(GET_RATE_DEBOUNCE_TIME);

  const convert: Convert = (from, to, amount) => {
    const displayDigits = displayOptions?.[from]?.display_digits;
    const totalDigits = displayOptions?.[from]?.total_digits;

    setError('');
    debounced(() => {
      if (+amount > 0 && getRate) {
        const correctingAmount = correctingTrailingNumbers(amount, displayDigits, totalDigits);

        getRate(from, to, correctingAmount)
          .then(res => {
            if (res) {
              setError('');
              const { total, subtotal } = res.data;
              setRate({ total, subtotal });
            }
          })
          .catch((e: ErrorType) => {
            const errorData = e?.data?.[from] || e?.data?.[0]?.[from];
            const min = errorData?.min || '0';
            const max = errorData?.max || '0';
            const minAmount = correctingTrailingNumbers(min, displayDigits, totalDigits);
            const maxAmount = correctingTrailingNumbers(max, displayDigits, totalDigits);
            const message = e?.message ? `${e.message} ` : '';
            const minError = minAmount ? `minimum ${minAmount}` : '';
            const maxError = maxAmount ? `maximum ${maxAmount}` : '';
            const prefix = minError || maxError ? `${from}: ` : '';
            const divider = minError && maxError ? `, ` : '';
            setError(`${message}${prefix}${minError}${divider}${maxError}`);
          });
      }
    });
  };

  return [convert, rate, error];
};

export default useConvert;
